import React, { useRef, useState, useEffect } from 'react';
import { renderStrokes } from '../drawingReplay';
import CircleTimer from './CircleTimer';
import UnresponsiveKickList from './UnresponsiveKickList';
import { audio } from '../audio';
import './GuessDrawing.css';

function GuessDrawing({ assignment, players, playerId, submittedCount, onSubmitGuess, timeLeft: externalTimeLeft, currentRound, totalRounds, alreadySubmitted, isHost, onKickPlayer, submissionError, onClearSubmissionError }) {
  const canvasRef = useRef(null);
  const headerRef = useRef(null);
  const [guess, setGuess] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(alreadySubmitted || false);
  const [isEditing, setIsEditing] = useState(false);
  const noLimit = (externalTimeLeft === 0);
  const [timeLeft, setTimeLeft] = useState(noLimit ? 0 : (externalTimeLeft || 45));
  const maxTimeRef = useRef(noLimit ? 0 : (externalTimeLeft || 45));

  // Keep header/timer visible when iOS keyboard opens
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handleResize = () => {
      if (headerRef.current) {
        headerRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };
    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, []);

  const canEdit = (!hasSubmitted || isEditing) && (noLimit || timeLeft > 0);

  // Render the drawing onto the canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !assignment?.drawing_data) return;

    const drawingData = assignment.drawing_data;
    const canvasConfig = drawingData.canvas || { width: 800, height: 600, background: '#ffffff' };

    canvas.width = canvasConfig.width || 800;
    canvas.height = canvasConfig.height || 600;

    const ctx = canvas.getContext('2d');
    renderStrokes(ctx, drawingData.strokes || [], Infinity, canvasConfig.background || '#ffffff');
  }, [assignment]);

  // Effect 1: Pure countdown (skip if no-limit)
  useEffect(() => {
    if (noLimit) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Timer tick sounds for last 10 seconds
  useEffect(() => {
    if (noLimit || hasSubmitted) return;
    if (timeLeft === 0) {
      audio.timerEnd();
    } else if (timeLeft <= 10) {
      audio.timerTick(timeLeft);
    }
  }, [timeLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: Auto-submit when timer reaches 0 (skip if no-limit)
  useEffect(() => {
    if (noLimit) return;
    if (timeLeft !== 0) return;
    if (!guess.trim()) return;
    onSubmitGuess(guess.trim());
    setHasSubmitted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  // The server rejected this guess (content moderation). Re-open the field so
  // the player can reword and resubmit instead of being stuck "submitted".
  useEffect(() => {
    if (submissionError) {
      setHasSubmitted(false);
      setIsEditing(true);
    }
  }, [submissionError]);

  const handleSubmit = () => {
    if (guess.trim()) {
      onSubmitGuess(guess.trim());
      audio.submitChime();
      setHasSubmitted(true);
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const waitingPlayers = players.filter(p => !p.submitted);
  const waitingText =
    waitingPlayers.length === 0 ? 'Almost done!' :
    waitingPlayers.length === 1 ? `Waiting for ${waitingPlayers[0].name}...` :
    waitingPlayers.length === 2 ? `Waiting for ${waitingPlayers[0].name} and ${waitingPlayers[1].name}...` :
    `Waiting for ${waitingPlayers.length} more...`;

  return (
    <div className="guess-container">
      <div className="guess-card">
        <div className="guess-header" ref={headerRef}>
          <div className="guess-header-text">
            <h1 className="guess-title"><span className="material-symbols-outlined" style={{fontSize: 'inherit', verticalAlign: 'middle', marginRight: '8px'}}>search</span>What is this?</h1>
            <p className="guess-subtitle">Look at the drawing and guess what it is!</p>
            {totalRounds > 0 && (
              <span className="guess-round-pill">
                round {currentRound + 1}/{totalRounds}
              </span>
            )}
          </div>
          <div className="guess-header-right">
            <CircleTimer timeLeft={timeLeft} maxTime={maxTimeRef.current} />
            {canEdit ? (
              <button className="submit-guess-button-top" onClick={handleSubmit} disabled={!guess.trim()}>Done</button>
            ) : hasSubmitted && (noLimit || timeLeft > 0) ? (
              <button className="edit-guess-button-top" onClick={() => setIsEditing(true)}>Edit</button>
            ) : hasSubmitted ? (
              <span className="guess-submitted-badge"><span className="material-symbols-outlined" style={{fontSize:'12px'}}>check_circle</span> Submitted</span>
            ) : null}
          </div>
        </div>

        <div className="guess-canvas-wrapper">
          {(!assignment?.drawing_data || !assignment.drawing_data.strokes || assignment.drawing_data.strokes.length === 0) ? (
            <div className="guess-canvas-empty">
              <span className="material-symbols-outlined" style={{ fontSize: 36, color: 'var(--color-text-light)', opacity: 0.5 }}>person_off</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-light)', fontWeight: 600 }}>Player didn't draw</span>
            </div>
          ) : (
            <canvas ref={canvasRef} className="guess-canvas" />
          )}
        </div>

        <div className="guess-form">
          <input
            type="text"
            className={`guess-input${!canEdit ? ' guess-input--locked' : ''}`}
            placeholder="Type your guess..."
            value={guess}
            onChange={(e) => {
              if (!canEdit) return;
              setGuess(e.target.value);
              if (submissionError) onClearSubmissionError?.();
            }}
            onKeyDown={handleKeyDown}
            maxLength={100}
            autoComplete="off"
            disabled={!canEdit}
            autoFocus={canEdit}
          />
          {submissionError && (
            <div className="guess-moderation-error" role="alert">
              <span className="material-symbols-outlined" style={{fontSize:'15px'}}>error</span>
              {submissionError}
            </div>
          )}
          {canEdit && <div className="char-count">{guess.length}/100</div>}
          {!canEdit && (
            <div className="submission-status">
              <span className="material-symbols-outlined" style={{fontSize:'13px'}}>
                {hasSubmitted ? 'check_circle' : 'timer'}
              </span>
              <span>{hasSubmitted ? ' Submitted' : " Time's up"}</span>
              <span className="submission-waiting"> · {waitingText}</span>
            </div>
          )}
          <UnresponsiveKickList players={players} isHost={isHost} onKickPlayer={onKickPlayer} />
        </div>

      </div>
    </div>
  );
}

export default GuessDrawing;
